import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataPagination } from "./DataPagination";

// Generic, reusable table. Renders a supplied empty slot when there is no data
// so every module shares one table + empty-state language. When `paginated` is
// set it manages its own page state and slices the data client-side.
// Pass `selectable` + `selectedIds` + `onSelectionChange` to get a checkbox column.
export function DataTable({
  columns = [],
  data = [],
  emptyState = null,
  rowKey = (row, i) => row.id ?? i,
  className,
  testId = "data-table",
  paginated = false,
  pageSize = 25,
  selectable = false,
  selectedIds = null,
  onSelectionChange = null,
  isRowSelectable = () => true,
}) {
  const [page, setPage] = useState(1);
  const total = data.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPage(1);
  }, [data]);

  const current = Math.min(page, pageCount);
  const pageData = useMemo(() => {
    if (!paginated) return data;
    const start = (current - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [paginated, data, current, pageSize]);

  const selection = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const selectableIds = useMemo(
    () => pageData.filter(isRowSelectable).map((r, i) => rowKey(r, i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageData]
  );
  const allSelected = selectable && selectableIds.length > 0 && selectableIds.every((id) => selection.has(id));
  const someSelected = selectable && !allSelected && selectableIds.some((id) => selection.has(id));

  const toggleRow = (id) => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(Array.from(next));
  };
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (allSelected) selectableIds.forEach((id) => next.delete(id));
    else selectableIds.forEach((id) => next.add(id));
    onSelectionChange(Array.from(next));
  };

  if (!data.length && emptyState) return emptyState;

  return (
    <div className="space-y-4">
      <div
        data-testid={testId}
        className={cn(
          "overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-premium backdrop-blur-sm",
          className
        )}
      >
        <Table>
          <TableHeader>
            <TableRow className="border-border/60 bg-muted/40 hover:bg-muted/40">
              {selectable && (
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows"
                    data-testid={`${testId}-select-all`}
                  />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    "h-11 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground",
                    col.align === "right" && "text-right",
                    col.className
                  )}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageData.map((row, i) => {
              const id = rowKey(row, i);
              const isSelected = selectable && selection.has(id);
              const rowSelectable = selectable && isRowSelectable(row);
              return (
                <TableRow
                  key={id}
                  data-selected={isSelected ? "true" : undefined}
                  className={cn(
                    "border-border/60 transition-colors",
                    isSelected ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/40"
                  )}
                >
                  {selectable && (
                    <TableCell className="w-10 pl-4">
                      {rowSelectable ? (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(id)}
                          aria-label="Select row"
                          data-testid={`${testId}-select-${id}`}
                        />
                      ) : null}
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        "py-3.5 text-sm text-foreground",
                        col.align === "right" && "text-right",
                        col.cellClassName
                      )}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {paginated && total > pageSize && (
        <DataPagination
          page={current}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          testId={`${testId}-pagination`}
        />
      )}
    </div>
  );
}
